import { useEffect, useState } from "react";
import TopNav from "../components/TopNav";
import { Link, useSearchParams } from "react-router-dom";
import {
  List,
  Avatar,
  ListItemText,
  ListItemAvatar,
  FormControl,
  Button,
  ListItemButton,
  Chip,
  ListItemIcon,
  Menu,
  Checkbox,
  ListItem,
  Slider,
  Breadcrumbs,
  Typography,
  Link as MUILink,
  ButtonGroup,
} from "@mui/material";
import { AddCircle, FilterList } from "@mui/icons-material";
import utilStyle from "./Utils.module.css";
import { formatMoney, statusToColor } from "../utils";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  APIProposal,
  APIProposalStatus,
  getAllProposals,
  getProposal,
} from "../api/proposals";
import SplitPageView from "../components/SplitPageView";
import ProductDetailsPage from "./ProductDetailsPage";
import ProposalDetailsPage from "./ProposalDetailsPage";
import { APICustomer, getCustomer } from "../api/customers";

function ProposalListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [proposalsData, setProposalsData] = useState<APIProposal[]>([]);
  const [curProposal, setCurProposal] = useState<APIProposal | null>(null);
  const [_proposalsData, _setProposalsData] = useState<APIProposal[]>([]);
  const [curCustomer, setCurCustomer] = useState<APICustomer | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    getAllProposals().then((v) => _setProposalsData(v));
    if (!id) {
      setCurProposal(null);
    } else {
      getProposal(id).then((v) => setCurProposal(v));
    }
  }, [id]);

  useEffect(() => {
    if (!curProposal?.customerId) {
      setCurCustomer(null);
    }
    getCustomer(curProposal?.customerId).then((v) => setCurCustomer(v));
  }, [curProposal?.customerId]);

  useEffect(() => {
    // Archived proposals only show under the Archived filter.
    let data = _proposalsData.filter((v) => !!v.archivedAt === showArchived);
    if (filter) {
      data = data.filter((v) => v.status == filter);
    }
    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
    data = data.filter((v) => {
      const haystack = [v.firstName, v.lastName, v.addressLine1, v.city]
        .join(" ")
        .toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
    setProposalsData(
      [...data].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    );
  }, [filter, search, showArchived, _proposalsData]);

  const updateFilter = (v: string) => () => {
    if (filter == v) {
      setFilter(null);
    } else {
      setFilter(v);
    }
  };

  return (
    <SplitPageView
      selected={
        !!searchParams.get("id") ||
        !!searchParams.get("cid") ||
        !!searchParams.get("new")
      }
      pageTitle="Proposals"
      search={search}
      onSearchChange={setSearch}
      contentList={
        <List style={{ padding: 0 }}>
          <ListItem sx={{ gap: 1, flexWrap: "wrap" }}>
            {["LEAD", "QUOTED", "WON", "DONE"].map((s) => (
              <Chip
                key={s}
                label={s[0] + s.slice(1).toLowerCase()}
                color={statusToColor(s as APIProposalStatus)}
                variant={filter == s ? "filled" : "outlined"}
                onClick={updateFilter(s)}
              />
            ))}
            <Chip
              label="Archived"
              variant={showArchived ? "filled" : "outlined"}
              onClick={() => setShowArchived(!showArchived)}
            />
          </ListItem>
          <ListItem>
            <Button
              component={Link}
              to="/proposals?new=1"
              variant="contained"
              startIcon={<AddCircle />}
              fullWidth
            >
              New Proposal
            </Button>
          </ListItem>
          {proposalsData.map((proposal) => (
            <ListItemButton
              component={Link}
              to={`/proposals?id=${proposal.id}`}
              key={proposal.id}
              selected={proposal.id === id}
            >
              <ListItemText
                primary={
                  `${proposal.firstName} ${proposal.lastName}`.trim() ||
                  "Unnamed customer"
                }
                secondary={[proposal.addressLine1, proposal.city]
                  .filter(Boolean)
                  .join(", ")}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 600 }}>
                  {formatMoney(proposal.totalAmount || 0)}
                </div>
                <Chip
                  size="small"
                  label={proposal.status}
                  color={statusToColor(proposal.status)}
                  variant="outlined"
                />
              </div>
            </ListItemButton>
          ))}
          {proposalsData.length === 0 && (
            <ListItem>
              <ListItemText
                secondary={
                  search || filter || showArchived
                    ? "No proposals match."
                    : "No proposals yet."
                }
              />
            </ListItem>
          )}
        </List>
      }
      breadcrumbs={
        id && (
          <Breadcrumbs aria-label="breadcrumb">
            <MUILink
              component={Link}
              underline="hover"
              color="inherit"
              to="/customers"
            >
              Customers
            </MUILink>
            <MUILink
              component={Link}
              underline="hover"
              color="inherit"
              to={`/customers?id=${curCustomer?.id}`}
            >
              {curCustomer?.firstName} {curCustomer?.lastName}
            </MUILink>
            <Typography sx={{ color: "text.primary" }}>Proposal</Typography>
          </Breadcrumbs>
        )
      }
      mainContent={<ProposalDetailsPage id={id || undefined} />}
    />
  );
}

export default ProposalListPage;
